import { Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getGridTab, setError, setGridTab } from 'state/reducers/customer-accounts.reducers';
import { TabsMenu } from 'components/TabsMenu';
import { SupportRequestsTable } from './SupportRequestsTable';
import { CompanySystemMessagesTable } from './CompanySystemMessagesTable';
import { BankAccountsTable } from './BankAccountsTable';
import { BeneficiariesTable } from './BeneficiariesTable';
import { ContactsTable } from './ContactsTable';
import { MoneyActivitiesTable } from './MoneyActivitiesTable';
import { ButtonComponent } from 'components/ButtonComponent';
import { initialTableState } from 'components/table/types';
import { pagesEnum } from 'constants/ui.constants';
import { pendingRequestsTypes } from 'constants/requests.constants';
import { useNavigate } from 'react-router-dom';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { verifyCloseAccount } from 'services/customer-accounts.service';
import _ from 'lodash';
import { customerInitialAction } from 'constants/customer-account.constants';
import React, { useEffect, useRef, useState } from 'react';
import { companyIsNotActive } from 'utils/index';
import * as XLSX from 'xlsx';
import { BalanceType } from 'constants/balance.constants';
import { format } from 'date-fns';
import { prepareTableDataForFileDownload } from './tablesConfig';

interface IProps {
	companyData: any;
}

export const BottomTables = ({ companyData }: IProps) => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const tableButtonStyle = { minWidth: '135px', flexDirection: 'row-reverse' };
	const currentTab = useAppSelector(getGridTab);
	const closeAccountPermission = hasPermission([permissionEnums.REMOVAL_ACCOUNT]);
	const [tabs, setTabs] = useState<any>([]);
	const isCompanyNotActive = companyIsNotActive(companyData?.basicData);
	const filteredDataForExcel: any = useRef();

	const handleDownloadClick = () => {
		try {
			prepareTableDataForFileDownload(
				filteredDataForExcel.current.data,
				t('page.customerAccounts.tables.tabs.moneyActivities'),
				companyData
			);
		} catch (error) {
			console.log('error', error);
			dispatch(setError({ exist: true }));
		}
	};

	const tabsInfo = [
		{
			displayTitle: t('page.customerAccounts.tables.tabs.moneyActivities'),
			table: (
				<MoneyActivitiesTable
					showFilter
					button={
						isCompanyNotActive ? (
							<></>
						) : (
							<Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
								{/* <ButtonComponent
									handleClick={() => {
										handleDownloadClick();
									}}
									text={t('report.reportDownload')}
									styleObj={{ ...tableButtonStyle }}
								/> */}
								<ButtonComponent
									disabled={false}
									handleClick={() =>
										navigate(
											`/${pagesEnum.COMPANY_FEATURE_CREATE}/${pendingRequestsTypes.BALANCES}`
										)
									}
									text={t('page.customerAccounts.tables.moneyActivities.action.addBalance')}
									icon={<img src="/icons/add.svg" style={{ marginLeft: '5px' }} />}
									styleObj={{ ...tableButtonStyle }}
								/>
								<ButtonComponent
									disabled={false}
									handleClick={() =>
										navigate(`/${pagesEnum.COMPANY_FEATURE_CREATE}/${pendingRequestsTypes.REFUNDS}`)
									}
									text={t('page.customerAccounts.tables.moneyActivities.action.create_refund_button')}
									styleObj={{ ...tableButtonStyle }}
								/>
							</Stack>
						)
					}
					dataRef={filteredDataForExcel}
				/>
			),
		},
		{
			displayTitle: t('page.customerAccounts.tables.tabs.contacts'),
			table: (
				<ContactsTable
					contacts={companyData?.contacts || []}
					button={
						isCompanyNotActive ? (
							<></>
						) : (
							<ButtonComponent
								disabled={false}
								handleClick={() =>
									navigate(`/${pagesEnum.COMPANY_FEATURE_CREATE}/${pendingRequestsTypes.CONTACTS}`)
								}
								text={t('page.customerAccounts.tables.contacts.action.addContact')}
								icon={<img src="/icons/add.svg" style={{ marginLeft: '5px' }} />}
								styleObj={{ ...tableButtonStyle }}
							/>
						)
					}
				/>
			),
		},
		{
			displayTitle: t('page.customerAccounts.tables.tabs.beneficiaries'),
			table: <BeneficiariesTable />,
		},
		{
			displayTitle: t('page.customerAccounts.tables.tabs.bankAccounts'),
			table: (
				<BankAccountsTable
					button={
						isCompanyNotActive ? (
							<></>
						) : (
							<ButtonComponent
								disabled={false}
								handleClick={() =>
									navigate(
										`/${pagesEnum.COMPANY_FEATURE_CREATE}/${pendingRequestsTypes.BANK_ACCOUNTS}`
									)
								}
								text={t('page.customerAccounts.tables.bankAccounts.action.addBankAccount')}
								icon={<img src="/icons/add.svg" style={{ marginLeft: '5px' }} />}
								styleObj={{ ...tableButtonStyle }}
							/>
						)
					}
				/>
			),
		},
		{
			displayTitle: t('page.customerAccounts.tables.tabs.supportRequests'),
			table: <SupportRequestsTable />,
		},
		...(hasPermission([permissionEnums.VIEW_SYSTEM_MESSAGES_COMPANY])
			? [
					{
						displayTitle: t('page.customerAccounts.tables.tabs.systemMessages'),
						table: <CompanySystemMessagesTable />,
					},
			  ]
			: []),
	];

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		dispatch(setGridTab(newValue));
	};

	useEffect(() => {
		if (companyData) {
			setTabs(tabsInfo);
		}
	}, [companyData]);

	return (
		<Stack
			spacing={3}
			sx={{
				position: 'relative',
				paddingBottom: tabs[currentTab]?.data?.length <= initialTableState.perPage ? '50px' : 'auto',
			}}
		>
			{companyData && tabs?.length > 0 && (
				<>
					<Typography variant="h4" sx={{ color: theme.palette.common.black, fontWeight: 700 }}>
						{t('page.customerAccounts.tables.title')}
					</Typography>
					<TabsMenu tabs={tabs} currentTab={currentTab} handleChange={handleChange} />
					{tabs[currentTab].table}
					{!isCompanyNotActive && (
						<Stack spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'right' }}>
							<ButtonComponent
								disabled={false}
								handleClick={() =>
									//sdsds
									navigate(
										`/${pagesEnum.COMPANY_FEATURE_CREATE}/${pendingRequestsTypes.WITHDRAWAL_SUPPORT}`
									)
								}
								text={t('page.customerAccounts.action.withdrawalBalance')}
								icon={<img src="/icons/refund_2.svg" style={{ marginLeft: '5px' }} />}
								styleObj={{
									background: theme.palette.root.forestGreen,
									boxShadow: 'none',
									flexDirection: 'row-reverse',
								}}
							/>
							{closeAccountPermission && (
								<ButtonComponent
									disabled={false}
									handleClick={() => {
										dispatch(verifyCloseAccount(companyData?.basicData?.id));
									}}
									text={t('page.customerAccounts.action.closeAccount')}
									icon={<img src="/icons/bankrupt.svg" style={{ marginLeft: '5px' }} />}
									styleObj={{
										color: theme.palette.root.orange_3,
										background: theme.palette.root.lightOrange_3,
										boxShadow: 'none',
										flexDirection: 'row-reverse',
										'&:hover': {
											color: theme.palette.root.basicWhite,
											'& img': { filter: 'brightness(2) saturate(0)' },
										},
									}}
								/>
							)}
						</Stack>
					)}
				</>
			)}
		</Stack>
	);
};
